/**
 * Auth Service
 *
 * Module-level functions for authentication operations. Each function calls
 * the Go API via erebusHttp (cookie-authenticated) and returns typed result
 * classes from auth-results.ts.
 *
 * On successful authentication (login or MFA verify), the legacy token is
 * automatically fetched before the promise resolves — callers don't need
 * to manage the legacy token lifecycle.
 */

import { erebusHttp } from './instances';
import {
  AuthMFAChallenge,
  AuthMFAEnrollRequired,
  AuthMustReset,
  AuthSuccess,
  toAuthResult,
  toResetFlowResult,
} from './auth-results';
import type { AuthResult, ResetFlowResult } from './auth-results';
import { clearLegacyToken, restoreLegacyToken } from './legacy-token-manager';
import { LegacyTokenError } from './legacy-token-error';
import type {
  LoginResponse,
  MFAMethod,
  MFASwitchResponse,
  MFAVerifyResponse,
  MFAEnrollResponse,
  MFAConfirmResponse,
  MFAMethodsResponse,
  MFABackupCodeStatusResponse,
  MFABackupCodesResponse,
  TrustedDevicesListResponse,
  RolesResponse,
  ForgotPasswordResponse,
  ResetFlowResponse,
} from './types';

/** How long to wait before the one retry of the legacy token fetch. */
const LEGACY_TOKEN_RETRY_DELAY_MS = 750;

/**
 * Gets the legacy token after the server accepted a sign-in step. Returns
 * true if it got one.
 *
 * It uses `restoreLegacyToken`, which does not log the user out when the
 * server is down or busy. One outage is retried once, after a short wait,
 * since it is often a blip (and 'offline' also covers a 429, which needs a
 * pause). A refusal is not retried.
 *
 * Not covered: if the server refuses the token (401 or 403), the auth failure
 * handler still runs, which logs the user out. That is out of scope here.
 */
async function fetchLegacyTokenAfterAuth(): Promise<boolean> {
  let outcome = await restoreLegacyToken();
  if (outcome === 'offline') {
    await new Promise((resolve) => setTimeout(resolve, LEGACY_TOKEN_RETRY_DELAY_MS));
    outcome = await restoreLegacyToken();
  }
  return outcome === 'ok';
}

/**
 * Authenticates a user by email or username and password.
 *
 * On success: session cookie is set by the browser, legacy token is fetched.
 * If the token cannot be fetched, throws LegacyTokenError (the user is not signed out).
 * On MFA required: returns AuthMFAChallenge for use with verifyMFA/resendMFA/switchMFAMethod.
 * On must reset: returns AuthMustReset for password change flow.
 * On MFA setup required: returns AuthMFAEnrollRequired for
 * beginEnrollWithChallenge/confirmEnrollWithChallenge.
 */
export async function login(identifier: string, password: string): Promise<AuthResult> {
  const { data } = await erebusHttp.post<LoginResponse>('/auth/login', {
    identifier,
    password,
  });

  const result = toAuthResult(data);

  if (result.isSuccess()) {
    if (!(await fetchLegacyTokenAfterAuth())) {
      throw new LegacyTokenError(result);
    }
  }

  return result;
}

/**
 * Verifies an MFA code (TOTP, SMS, or email) to complete login.
 *
 * With `backupCode` true, `code` is one of the user's single-use backup codes
 * instead, for a TOTP or SMS challenge (PRX-2159). The server never trusts the
 * device after a backup code, whatever `trustDevice` says.
 *
 * Returns AuthResult because MFA verify can also return must_reset, or
 * mfa_enroll_required: an email-only user whose role requires TOTP or SMS
 * passes the email code, then must set one up (PRX-2355).
 * On success: session cookie is set, legacy token is fetched.
 */
export async function verifyMFA(
  challenge: AuthMFAChallenge,
  code: string,
  trustDevice?: boolean,
  backupCode?: boolean,
): Promise<AuthResult> {
  const { data } = await erebusHttp.post<MFAVerifyResponse>('/auth/mfa/verify', {
    challengeToken: challenge.challengeToken,
    code,
    ...(trustDevice != null && { trustDevice }),
    ...(backupCode && { backupCode }),
  });

  // No session yet, so there is no legacy token to fetch.
  if (data.status === 'mfa_enroll_required') {
    return new AuthMFAEnrollRequired(data);
  }

  const result = toMFAStepResult(data, '/auth/mfa/verify');

  if (result.isSuccess()) {
    if (!(await fetchLegacyTokenAfterAuth())) {
      throw new LegacyTokenError(result);
    }
  }

  return result;
}

/**
 * Maps the end of an MFA step (verify, or setup during login) to a result. It
 * can only be success or must_reset; anything else throws.
 */
function toMFAStepResult(data: MFAVerifyResponse, endpoint: string): AuthSuccess | AuthMustReset {
  // MFAVerifyResponse shares LoginStatus — map explicitly to avoid unsafe casts
  switch (data.status) {
    case 'success':
      return new AuthSuccess(data);
    case 'must_reset':
      return new AuthMustReset(data);
    case 'mfa_required':
    case 'select_account':
    case 'mfa_enroll_required':
      throw new Error(`Unexpected ${data.status} status from ${endpoint}`);
    default: {
      const _exhaustive: never = data.status;
      throw new Error(`Unknown MFA verify status: ${_exhaustive}`);
    }
  }
}

// =============================================================================
// MFA setup during login (PRX-2355) — no session, setup-token based
// =============================================================================

/**
 * Starts TOTP or SMS setup for a user whose login returned
 * mfa_enroll_required. The setup token stands in for the session they do not
 * have yet. TOTP returns the secret and otpauth URL; SMS sends a code.
 */
export async function beginEnrollWithChallenge(
  setup: AuthMFAEnrollRequired,
  method: MFAMethod,
  phone?: string,
): Promise<MFAEnrollResponse> {
  const { data } = await erebusHttp.post<MFAEnrollResponse>('/auth/mfa/enroll-challenge', {
    challengeToken: setup.challengeToken,
    method,
    ...(phone && { phone }),
  });
  return data;
}

/**
 * Confirms that setup with the code the user typed, which completes the login.
 *
 * On success the session cookie is set and the legacy token is fetched, the
 * same as after verifyMFA. On must_reset the user still has to change their
 * password: MFA first, then the password.
 */
export async function confirmEnrollWithChallenge(
  setup: AuthMFAEnrollRequired,
  methodId: string,
  code: string,
): Promise<AuthSuccess | AuthMustReset> {
  const endpoint = '/auth/mfa/enroll-challenge/confirm';
  const { data } = await erebusHttp.post<MFAVerifyResponse>(endpoint, {
    challengeToken: setup.challengeToken,
    methodId,
    code,
  });

  const result = toMFAStepResult(data, endpoint);

  if (result.isSuccess()) {
    if (!(await fetchLegacyTokenAfterAuth())) {
      throw new LegacyTokenError(result);
    }
  }

  return result;
}

/**
 * Completes a password reset using the challenge token from a must_reset result.
 *
 * On success the user is signed in: the backend sets the session cookie, and the
 * legacy token is fetched here so calls to the Java API work straight away, the
 * same as after login or MFA verify.
 *
 * Unlike those two, a legacy-token failure does not throw. The password has
 * already changed and the reset token is single-use and now spent, so an error
 * would report a failure that did not happen and offer a retry that cannot
 * succeed. The caller finds out the ordinary way — the next API call fails and
 * sends them to log in.
 *
 * The backend also revokes every trusted device, so the next login from anywhere
 * will ask for an MFA code.
 */
export async function resetPassword(challengeToken: string, newPassword: string): Promise<void> {
  await erebusHttp.post('/auth/reset-password', {
    challengeToken,
    newPassword,
  });

  // Result deliberately ignored — see above. A failure here is not a failed
  // reset, and the next API call surfaces it.
  await fetchLegacyTokenAfterAuth();
}

// =============================================================================
// Self-serve forgot password (PRX-2033)
// =============================================================================

/**
 * Starts a self-serve reset and returns the token for the code step.
 *
 * The answer is the same whether the address has no accounts, one or five, so
 * there is nothing here to tell a caller whether it was recognised — and
 * nothing a screen can say beyond "if that address is on file, we sent a code".
 *
 * Calling this again is also how a code is resent: there is no resend endpoint.
 * Each call mints a fresh code and a fresh token, so the caller must keep the
 * token this returns and drop the one it had. Codes already sent stay valid.
 */
export async function requestPasswordReset(email: string): Promise<string> {
  const { data } = await erebusHttp.post<ForgotPasswordResponse>('/auth/forgot-password', {
    email,
  });

  return data.challengeToken;
}

/**
 * Submits the emailed code and moves the reset to its next step.
 *
 * Returns a ResetAccountChoice when the address matches more than one account,
 * an AuthMFAChallenge when a second factor is owed, or an AuthMustReset when
 * the user can go straight to choosing a password.
 *
 * Every failure looks the same from out here — a wrong code, a spent code, an
 * expired one and an address with no accounts all come back as the same 401.
 * That is deliberate; do not try to tell them apart.
 */
export async function verifyResetCode(
  challengeToken: string,
  code: string,
): Promise<ResetFlowResult> {
  const { data } = await erebusHttp.post<ResetFlowResponse>('/auth/forgot-password/verify', {
    challengeToken,
    code,
  });

  return toResetFlowResult(data, '/auth/forgot-password/verify');
}

/**
 * Names which of several matching accounts the reset applies to.
 *
 * Only valid with the token from a ResetAccountChoice. The choice is made once:
 * a second select_account here would mean the first did not stick, so it throws
 * rather than looping the user back to a picker.
 */
export async function selectResetAccount(
  challengeToken: string,
  userId: string,
): Promise<ResetFlowResult> {
  const { data } = await erebusHttp.post<ResetFlowResponse>('/auth/forgot-password/account', {
    challengeToken,
    userId,
  });

  if (data.status === 'select_account') {
    throw new Error('Unexpected select_account status from /auth/forgot-password/account');
  }

  return toResetFlowResult(data, '/auth/forgot-password/account');
}

/** Resends the MFA verification code for SMS/email challenges. */
export async function resendMFA(challenge: AuthMFAChallenge): Promise<void> {
  await erebusHttp.post('/auth/mfa/resend', {
    challengeToken: challenge.challengeToken,
  });
}

/**
 * Switches to a different MFA method mid-login.
 * Returns a new AuthMFAChallenge with the updated challenge token and delivery status.
 */
export async function switchMFAMethod(
  challenge: AuthMFAChallenge,
  method: MFAMethod,
): Promise<AuthMFAChallenge> {
  const { data } = await erebusHttp.post<MFASwitchResponse>('/auth/mfa/switch', {
    challengeToken: challenge.challengeToken,
    method,
  });

  return new AuthMFAChallenge(data);
}

/**
 * Logs out the current session (or all sessions if allDevices is true).
 * Clears the in-memory legacy token regardless of whether the API call succeeds.
 */
export async function logout(options?: { allDevices?: boolean }): Promise<void> {
  try {
    await erebusHttp.post('/auth/logout', {
      allDevices: options?.allDevices ?? false,
    });
  } finally {
    clearLegacyToken();
  }
}

// ---------------------------------------------------------------------------
// MFA Enrollment & Management (authenticated, session-cookie required)
// ---------------------------------------------------------------------------

/** Begins enrollment of a new MFA method. TOTP returns secret + otpauth URL; SMS/email sends a code. */
export async function enrollMFA(method: MFAMethod, phone?: string): Promise<MFAEnrollResponse> {
  const { data } = await erebusHttp.post<MFAEnrollResponse>('/auth/mfa/enroll', {
    method,
    ...(phone && { phone }),
  });
  return data;
}

/** Confirms MFA enrollment by verifying the code. Auto-sets as preferred if first method. */
export async function confirmMFAEnrollment(
  methodId: string,
  code: string,
): Promise<MFAConfirmResponse> {
  const { data } = await erebusHttp.post<MFAConfirmResponse>('/auth/mfa/enroll/confirm', {
    methodId,
    code,
  });
  return data;
}

/** Returns all verified MFA methods for the authenticated user. */
export async function listMFAMethods(): Promise<MFAMethodsResponse> {
  const { data } = await erebusHttp.get<MFAMethodsResponse>('/auth/mfa/methods');
  return data;
}

/** Sets the specified verified MFA method as the default for login challenges. */
export async function setPreferredMFA(methodId: string): Promise<void> {
  await erebusHttp.put(`/auth/mfa/methods/${methodId}/preferred`);
}

/** Removes an enrolled MFA method. Blocks removal of the last verified method. */
export async function removeMFAMethod(methodId: string): Promise<void> {
  await erebusHttp.delete(`/auth/mfa/methods/${methodId}`);
}

/** Returns how many unused MFA backup codes the user has left. */
export async function getBackupCodeCount(): Promise<number> {
  const { data } = await erebusHttp.get<MFABackupCodeStatusResponse>('/auth/mfa/backup-codes');
  return data.remaining;
}

/**
 * Replaces the user's MFA backup codes with a new set and returns it. Every
 * old code stops working. The server never shows these codes again.
 */
export async function regenerateBackupCodes(): Promise<string[]> {
  const { data } = await erebusHttp.post<MFABackupCodesResponse>('/auth/mfa/backup-codes');
  return data.codes;
}

// ---------------------------------------------------------------------------
// Trusted Device Management (authenticated, session-cookie required)
// ---------------------------------------------------------------------------

/** Returns all non-expired trusted devices for the authenticated user. */
export async function listTrustedDevices(): Promise<TrustedDevicesListResponse> {
  const { data } = await erebusHttp.get<TrustedDevicesListResponse>('/auth/trusted-devices');
  return data;
}

/** Revokes a single trusted device. */
export async function revokeTrustedDevice(deviceId: string): Promise<void> {
  await erebusHttp.delete(`/auth/trusted-devices/${deviceId}`);
}

/** Revokes all trusted devices for the authenticated user. */
export async function revokeAllTrustedDevices(): Promise<void> {
  await erebusHttp.delete('/auth/trusted-devices');
}

// ---------------------------------------------------------------------------
// Roles (authenticated, session-cookie required)
// ---------------------------------------------------------------------------

/** Returns the authenticated (cookie-session) user's scoped roles. Cookie auth only. */
export async function getMyRoles(): Promise<RolesResponse> {
  const { data } = await erebusHttp.get<RolesResponse>('/auth/me/roles');
  return data;
}
